import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormLabelProps,
  NumberInputProps,
} from "@chakra-ui/react";
import { BaseProps, NumberInputControl } from "formik-chakra-ui";
import * as React from "react";

type Props = {
  name: string;
  label: string;
  onChangeEvent?(valueAsString: string, valueAsNumber: number): void;
  onBlur?: () => void;
  isDisabled?: boolean;
  minValue?: number;
  maxValue?: number;
  value?: number;
  pattern?: string;
  showStepper?: boolean;
  error?: string;
  numberInputProps?: NumberInputProps;
  labelProps?: FormLabelProps;
} & BaseProps;

/**
 * A Formik-integrated number input component with optional min/max constraints.
 *
 * @param onChangeEvent - Optional callback when value changes (callback must handle Formik state updates)
 *
 * @example
 * // With onChangeEvent - caller must update Formik state
 * const { setFieldValue } = useFormikContext();
 * <NumberInput
 *   name="age"
 *   label="Age"
 *   onChangeEvent={(valueAsString, valueAsNumber) => {
 *     setFieldValue("age", valueAsString);
 *   }}
 * />
 *
 * @example
 * // Without onChangeEvent - Formik handles state automatically via NumberInputControl
 * <NumberInput name="quantity" label="Quantity" />
 */
export const NumberInput: React.FC<Props> = ({
  name,
  label,
  onChangeEvent,
  onBlur,
  isDisabled,
  isRequired,
  minValue,
  maxValue,
  value,
  showStepper,
  error,
  labelProps,
  numberInputProps,
  ...rest
}) => {
  return (
    <FormControl
      isInvalid={Boolean(error)}
      isRequired={isRequired}
      height="100%"
    >
      <FormLabel htmlFor={name} {...labelProps}>
        {label}
      </FormLabel>
      <NumberInputControl
        {...rest}
        id={name}
        name={name}
        showStepper={showStepper}
        numberInputProps={{
          min: minValue,
          max: maxValue,
          value,
          onChange: onChangeEvent,
          onBlur: onBlur,
          ...numberInputProps,
        }}
      ></NumberInputControl>
      {error && <FormErrorMessage {...rest}>{error}</FormErrorMessage>}
    </FormControl>
  );
};
